import { useCallback, useMemo, useRef } from 'react';

import styled, { css } from 'styled-components';
import {
  useRfqBuilderActions,
  useRfqBuilderOptionLegView,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';

import TransparentSelectField from '#/components/UnifiedRfqs/components/TransparentSelectField';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { FormOption } from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/types';

export default function StrikeSelector() {
  const view = useRfqBuilderOptionLegView();
  const { index, strike, delta, isHedge } = view;
  const { strikeIndex, selectedOption, options, isValid, isDisabled } = strike;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { selectOptionLegStrike } = useRfqBuilderActions();
  const { strategy, strategyShortcut } = useRfqBuilderView();
  const trackAction = useTrackAction();
  const strategyCode =
    strategyShortcut.selectedOption?.value ??
    strategy.selectedOption?.value ??
    null;

  const handleOnChange = useCallback(
    (option: FormOption<string> | null) => {
      trackAction('update strategy leg', {
        strategyCode,
        strike: option?.value ?? null,
        isHedge,
      });
      if (option != null) selectOptionLegStrike(index, option.value);
    },
    [index, selectOptionLegStrike, strategyCode, trackAction, isHedge],
  );

  const isMobile = useIsMobile();

  // Scroll into similar strike index when user opens the menu
  const handleMenuOpen = useCallback(() => {
    if (selectedOption != null || strikeIndex === -1) return;
    setTimeout(() => {
      containerRef.current
        ?.querySelector(optionSelector(strikeIndex))
        ?.scrollIntoView({ block: 'center' });
    }, 0);
  }, [selectedOption, strikeIndex]);

  return useMemo(
    () => (
      <TransparentSelectFieldContainer
        strikeIndex={strikeIndex}
        ref={containerRef}
        height={selectedOption && Boolean(delta) ? 25 : undefined}
      >
        <TransparentSelectField
          aria-label="Select strike"
          onMenuOpen={handleMenuOpen}
          options={options}
          value={selectedOption}
          invalid={!isValid}
          isDisabled={isDisabled}
          onChange={handleOnChange}
          isSearchable={!isMobile}
        />
        {isValid && Boolean(delta) && <Delta>∆ {delta}</Delta>}
      </TransparentSelectFieldContainer>
    ),
    [
      strikeIndex,
      selectedOption,
      delta,
      options,
      isValid,
      isDisabled,
      handleOnChange,
      isMobile,
      handleMenuOpen,
    ],
  );
}

const optionSelector = (index: number) =>
  `div[role='option']:nth-child(${index})`;

const TransparentSelectFieldContainer = styled.div<{
  height?: number;
  strikeIndex: number;
}>`
  .transparent-select-container {
    height: ${({ height }) => `${height}px` || 'auto'};
  }

  /* Highlight strike index and the other 2 surrounding strikes */
  ${({ strikeIndex }) =>
    strikeIndex !== -1 &&
    css`
      ${optionSelector(strikeIndex - 1)}, ${optionSelector(strikeIndex + 1)} {
        background-color: rgba(117, 171, 242, 0.2);
        &:hover {
          background-color: rgba(117, 171, 242, 0.4);
        }
      }
      ${optionSelector(strikeIndex)} {
        background-color: rgba(117, 171, 242, 0.25);
        &:hover {
          background-color: rgba(117, 171, 242, 0.4);
        }
      }
      ${optionSelector(strikeIndex - 2)}, ${optionSelector(strikeIndex + 2)} {
        background-color: rgba(117, 171, 242, 0.15);
        &:hover {
          background-color: rgba(117, 171, 242, 0.4);
        }
      }
    `}
`;

const Delta = styled.div`
  opacity: 0.6;
  font-size: 10px;
  padding-left: 10px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    text-align: right;
    padding-right: 1rem;
  }
`;
